// @flow
import axios from 'axios';
import createError from 'axios/lib/core/createError';

import * as cc from 'common/constants';
import { errorDialog } from 'common/components/dialog';
import { isFetching } from 'common/actions';
import { queryString } from 'common/utils';
import { store } from 'store/configureStore';
import * as authOperations from 'modules/auth/operations';
import { buildAuthorizationHeader } from 'modules/auth/helpers';

import BackendError from './BackendError';

type RequestId = string | number;

export type CallApiConfig = {|
  method?: cc.FetchMethod,
  url: string,

  params?: { [key: string]: any },
  data?: { [key: string]: any },
  cancelToken?: axios.CancelToken,

  requestId?: RequestId,
  preventDefaultErrorHandler?: boolean,
  withError?: boolean,
  forceResolve?: boolean,
  forceResolveSuccessful?: boolean,
|};

type ApiRequestParams = {
  method: cc.FetchMethod,
  url: string,
  cancelToken?: axios.CancelToken,

  params?: { [key: string]: any },
  data?: { [key: string]: any },
};

export type ApiResponse = { data: any };

const authRequests = [cc.REFRESH_TOKEN_ID, cc.GET_TOKENS_ID];

/**
 * Display errors
 *
 * @param  {array, object, string} errors
 */
const displayErrors = (errors) => {
  const message = (() => {
    if (errors instanceof Array) {
      return errors.join('<br />');
    } else if (errors instanceof Object) {
      return Object.keys(errors)
        .map((key) => errors[key][0])
        .join('<br />');
    }

    return errors;
  })();

  return errorDialog({
    message,
    isHtml: true,
  });
};

const { dispatch } = store;

const apiInstance = axios.create();

// Default axios settings
apiInstance.defaults.headers['X-Requested-With'] = 'XMLHttpRequest';

// Request interceptors
apiInstance.interceptors.request.use(
  (config): any => {
    const authHeader = buildAuthorizationHeader();

    return !authHeader
      ? config
      : {
          ...config,
          headers: {
            ...config.headers,
            Authorization: authHeader,
          },
        };
  },
);

// App requests
const activeRequests = new Map();
const stackedRequests = new Map();
const deferredRequests = [];

/**
 * Replay all deferred requests
 */
export const replayRequests = () => {
  const requests = [];
  while (deferredRequests.length > 0) {
    requests.push(deferredRequests.pop());
  }

  return Promise.all(
    requests.map(async ({ config, resolve, reject, callback }) => {
      try {
        const response = await apiInstance.request(config);
        callback();

        return resolve(response);
      } catch (err) {
        return reject(err);
      }
    }),
  );
};

const replayStackedRequests = (requestId, callback) => {
  if (!requestId) {
    return;
  }

  const replayRequest = stackedRequests.get(requestId) || [];

  replayRequest.forEach(callback);

  stackedRequests.delete(requestId);
};

const deferRequest = (requestId, config, resolve, reject) => {
  deferredRequests.push({
    config,
    resolve,
    reject,
    callback: () => {
      if (requestId) {
        dispatch(isFetching(false, requestId));
      }
    },
  });
};

const getErrorsFromResponse = (response) => {
  const { data, status } = response;
  const { errors = {}, message } = data;

  if (!errors.length && !Object.keys(errors).length) {
    if (message) {
      return [message];
    }

    if (status === cc.KNOWN_HTTP_STATUSES.FORBIDDEN) {
      return ["You don't have permission to perform this operation"];
    }

    return [cc.TEXT_SERVER_ERROR];
  }

  return errors;
};

const getFirstError = (errors: string | string[] | { [key: string]: string }) => {
  if (typeof errors === 'string') {
    return errors;
  }

  if (Array.isArray(errors)) {
    return errors[0];
  }

  const keys = Object.keys(errors);
  if (keys.length) {
    return errors[keys[0]];
  }

  return undefined;
};

function transformError(err) {
  const { response } = err;

  if (response) {
    const errors = getErrorsFromResponse(response);
    return new BackendError(response.status, errors, response.data, getFirstError(errors));
  }

  return err;
}

const reauthorizeAndReplayRequest = (requestId, config) =>
  new Promise(async (resolve, reject) => {
    deferRequest(requestId, config, resolve, reject);

    dispatch(authOperations.refreshToken());
  });

/**
 * Response handler
 *
 * @param {object} { requestId }
 * @param {object} response
 * @return {object}
 */
const responseHandler = ({
  requestId,
  preventDefaultErrorHandler,
  forceResolve,
  forceResolveSuccessful,
}) => (response) =>
  new Promise((resolve, reject) => {
    const { data = {} } = response;
    const { errors = {} } = data;

    if (requestId) {
      dispatch(isFetching(false, requestId));
    }

    if (errors.length || Object.keys(errors).length) {
      displayErrors(errors);

      if (forceResolve || forceResolveSuccessful) {
        resolve(response);
      } else if (preventDefaultErrorHandler) {
        const firstError = errors[0] || errors[Object.keys(errors)[0]];
        reject(createError(firstError, response.config, null, response.request, response));
      }

      return;
    }

    resolve(response);
  });

/**
 * Errors handler
 *
 * @param {object} { requestId, preventDefaultErrorHandler }
 * @param {object} error
 * @return {Promise}
 */
const errorsHandler = (options) => (error) => {
  const { requestId, preventDefaultErrorHandler, forceResolve, withError } = options;

  return new Promise((resolve, reject) => {
    const { response } = error;
    const isCancel = axios.isCancel(error);

    if (response) {
      const { config, status } = response;

      if (status === cc.KNOWN_HTTP_STATUSES.UNAUTHORIZED && !authRequests.includes(requestId)) {
        resolve(reauthorizeAndReplayRequest(requestId, config));
        return;
      }
    }

    if (requestId) {
      dispatch(isFetching(false, requestId));
    }

    if (preventDefaultErrorHandler) {
      reject(transformError(error));
      return;
    }

    if (response) {
      displayErrors(getErrorsFromResponse(response));
    } else if (!isCancel) {
      displayErrors(['You have some problems in your network']);
    }

    if (forceResolve) {
      resolve();
    } else if (withError && !isCancel) {
      reject(transformError(error));
    }
  });
};

const sendRequest = (params: any, handlerConfig: *) =>
  apiInstance.request(params).then(responseHandler(handlerConfig), errorsHandler(handlerConfig));

/**
 * Requests cancellation
 *
 * @param {string/number} requestId
 */
export const CancelRequest = (requestId: RequestId) => {
  if (requestId && activeRequests.has(requestId)) {
    const cancelFn = activeRequests.get(requestId);
    if (typeof cancelFn === 'function') {
      cancelFn();
    }
    activeRequests.delete(requestId);
  }
};

const defaultHandler = (apiPromise) => apiPromise;

/**
 * Call API
 *
 * @param {object} pms - request's params
 */
export function CallApi<T>(
  config: CallApiConfig,
  handler?: (response: Promise<ApiResponse>) => Promise<T> = defaultHandler,
): Promise<T> {
  const {
    requestId,
    preventDefaultErrorHandler,
    withError,
    forceResolve,
    forceResolveSuccessful,
    ...rest
  } = config;

  const params: ApiRequestParams = {
    method: 'GET',
    paramsSerializer(rawParams) {
      return queryString.stringify(rawParams, { arrayFormat: 'bracket' });
    },
    ...rest,
  };

  if (!params.url) {
    console.error('CallApi without URL is not working! Config: ', config);

    return Promise.reject(new Error('CallApi without URL is not working!'));
  }

  // Requests cancellation
  if (requestId) {
    if (activeRequests.has(requestId) && authRequests.includes(requestId)) {
      const requests = stackedRequests.get(requestId) || [];

      return new Promise((resolve, reject) =>
        stackedRequests.set(requestId, [...requests, { resolve, reject }]),
      );
    }

    CancelRequest(requestId);

    params.cancelToken = new axios.CancelToken((cancelFn) => {
      activeRequests.set(requestId, cancelFn);
    });

    // Fetching status active
    dispatch(isFetching(true, requestId));
  }

  const handlerConfig = {
    requestId,
    preventDefaultErrorHandler,
    withError,
    forceResolve,
    forceResolveSuccessful,
  };

  // Send request and replay stackedRequests
  return handler(
    sendRequest(params, handlerConfig).then(
      (response?: ApiResponse) => {
        if (!response) {
          throw new Error('Empty response');
        }

        const resultResponse = response;

        activeRequests.delete(requestId);
        replayStackedRequests(requestId, (i) => i.resolve(resultResponse));

        return resultResponse;
      },
      (err) => {
        activeRequests.delete(requestId);
        replayStackedRequests(requestId, (i) => i.reject(err));

        throw err;
      },
    ),
  );
}

export default CallApi;



// WEBPACK FOOTER //
// ./src/common/network/callApi.js